import React, { useEffect, useMemo, useState } from 'react';

const LOADING_HEROES = [
  '/Hero-1.jpg',
  '/Hero-2.jpg',
  '/Hero-3.jpg',
  '/Hero-4.jpg',
  '/Hero-5.jpg',
  '/Hero-6.png',
  '/Hero-7.png',
  '/Hero-8.png',
  '/Hero-9.png',
  '/Hero-10.png',
  '/Hero-11.png',
];

function pickLoadingHero() {
  try {
    const currentIndex = Number(window.sessionStorage.getItem('krinkelsLoadingHeroIndex') || '-1');
    const nextIndex = Number.isFinite(currentIndex)
      ? (currentIndex + 1) % LOADING_HEROES.length
      : Math.floor(Math.random() * LOADING_HEROES.length);
    window.sessionStorage.setItem('krinkelsLoadingHeroIndex', String(nextIndex));
    return LOADING_HEROES[nextIndex];
  } catch {
    return LOADING_HEROES[new Date().getSeconds() % LOADING_HEROES.length];
  }
}

export default function LoadingScreen({ message = 'Laden...', fullScreen = true, compact = false, className = '', progress = null }) {
  const loadingLogo = '/Krinkels-logo.png';
  const loadingHero = useMemo(() => (fullScreen && !compact ? pickLoadingHero() : null), [fullScreen, compact]);
  const hasProgress = Number.isFinite(progress);
  const clampedProgress = useMemo(() => {
    if (!hasProgress) return null;
    return Math.max(0, Math.min(100, Math.round(progress)));
  }, [hasProgress, progress]);

  const [simulatedProgress, setSimulatedProgress] = useState(8);
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    if (hasProgress) return undefined;

    setSimulatedProgress(8);
    const intervalId = setInterval(() => {
      setSimulatedProgress((prev) => {
        if (prev >= 94) return 94;
        if (prev < 60) return prev + 3;
        if (prev < 85) return prev + 1.5;
        if (prev < 92) return prev + 0.6;
        return Math.min(94, prev + 0.2);
      });
    }, 220);

    return () => clearInterval(intervalId);
  }, [hasProgress, message]);

  useEffect(() => {
    setStalled(false);
    const timeoutId = setTimeout(() => setStalled(true), 12000);
    return () => clearTimeout(timeoutId);
  }, [message]);

  useEffect(() => {
    if (!stalled || hasProgress) return;
    setSimulatedProgress((prev) => Math.max(prev, 99));
  }, [stalled, hasProgress]);

  const effectiveProgress = hasProgress ? clampedProgress : Math.round(simulatedProgress);

  const overlayClassName = [
    'app-loading-overlay',
    fullScreen ? '' : 'app-loading-overlay-inline',
    className,
  ].filter(Boolean).join(' ');

  const cardClassName = [
    'app-loading-card',
    compact ? 'app-loading-card-compact' : '',
  ].filter(Boolean).join(' ');

  const showLogo = !String(className || '').includes('thumbnail-loader');

  return (
    <div
      className={overlayClassName}
      role="status"
      aria-live="polite"
      aria-label={message}
      style={loadingHero ? { '--loading-hero-image': `url("${loadingHero}")` } : undefined}
    >
      <div className={cardClassName}>
        {showLogo && (
          <img
            src={loadingLogo}
            alt="Krinkels"
            className={`app-loading-logo ${compact ? 'app-loading-logo-compact' : ''}`.trim()}
            loading="eager"
            decoding="async"
          />
        )}
        <span className="app-loading-wheel" aria-hidden="true" />
        <p className="app-loading-text">{message}</p>
        {stalled && (
          <div className="app-loading-warning">Het laden duurt langer dan normaal. Controleer je verbinding of ververs de pagina.</div>
        )}
        <div className="app-loading-bar" aria-hidden="true">
          <div
            className="app-loading-bar-fill determinate"
            style={{ width: `${effectiveProgress}%` }}
          />
        </div>
        <span className="app-loading-percent" aria-hidden="true">{effectiveProgress}%</span>
      </div>
    </div>
  );
}
